import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ForgotPasswordSchema } from '@/lib/schemas/auth';
import { apiCall, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CosmoBudLogo } from '@/components/CosmoBudLogo';
import { ParticlesBackground } from '@/components/ParticlesBackground';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const RATE_LIMIT_COOLDOWN_SECONDS = 60;
const RATE_LIMIT_STORAGE_KEY = 'forgot_password_cooldown';

function getCooldownEndTime(): number | null {
  const stored = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
  if (!stored) return null;
  const endTime = parseInt(stored, 10);
  if (isNaN(endTime) || endTime < Date.now()) {
    localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
    return null;
  }
  return endTime;
}

function setCooldown(): void {
  const endTime = Date.now() + RATE_LIMIT_COOLDOWN_SECONDS * 1000;
  localStorage.setItem(RATE_LIMIT_STORAGE_KEY, endTime.toString());
}

function errorMessage(code: string): string {
  switch (code) {
    case 'USER_NOT_FOUND':
      return 'No account found with this email address.';
    case 'RATE_LIMITED':
      return 'Too many requests. Please try again later.';
    default:
      return 'Could not process request. Please try again.';
  }
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [cooldownEnd, setCooldownEnd] = useState<number | null>(() => getCooldownEndTime());
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  useEffect(() => {
    if (!cooldownEnd) {
      setSecondsRemaining(0);
      return;
    }

    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((cooldownEnd - Date.now()) / 1000));
      setSecondsRemaining(remaining);

      if (remaining === 0) {
        setCooldownEnd(null);
        localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [cooldownEnd]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorText(null);
    setFieldError(null);

    // Check client-side cooldown
    const endTime = getCooldownEndTime();
    if (endTime) {
      const remaining = Math.ceil((endTime - Date.now()) / 1000);
      setCooldownEnd(endTime);
      setSecondsRemaining(remaining);
      return;
    }

    const parsed = ForgotPasswordSchema.safeParse({ email });
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      setFieldError(first?.message ?? 'Invalid email.');
      return;
    }

    setBusy(true);
    try {
      await apiCall<{ message: string }>('/auth/forgot-password', {
        method: 'POST',
        body: { email: parsed.data.email },
      });
      // Set cooldown after successful request
      setCooldown();
      setCooldownEnd(getCooldownEndTime());
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 4000);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.errorCode === 'RATE_LIMITED') {
          // Server rate limit hit - set cooldown
          setCooldown();
          setCooldownEnd(getCooldownEndTime());
        }
        setErrorText(errorMessage(err.errorCode));
      } else {
        setErrorText('Network error. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Desktop: Left side brand area (50%) */}
      <div className="hidden sm:flex flex-1 relative items-center justify-center p-8 overflow-hidden bg-white">
        <ParticlesBackground />
        <div className="relative z-10 text-center">
          <img src="/cosmobud-hero.png" alt="CosmoBud" className="w-64 h-auto drop-shadow-lg" />
        </div>
      </div>

      {/* Mobile: Full width form / Desktop: Right side (50%) */}
      <div className="flex flex-col w-full sm:w-1/2 min-h-screen sm:bg-gradient-to-br sm:from-blue-600 sm:to-violet-600">
        <div className="flex flex-col min-h-screen bg-background sm:bg-transparent px-4 pt-6 pb-6 sm:px-8 sm:py-0 sm:place-items-center sm:justify-center">
      <Card className="w-full rounded-none sm:rounded-lg sm:max-w-md sm:bg-white sm:shadow-2xl">
        <CardHeader className="px-4 pt-6 pb-4 sm:pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex justify-center sm:hidden">
              <CosmoBudLogo size="custom" showText={false} customSize="w-[60px] h-[60px]" />
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <CardTitle className="text-lg sm:text-xl">Reset password</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Enter your email and we'll send you a temporary password.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-6 sm:pb-4">
          {success && (
            <Alert variant="success" className="mb-4">
              <AlertTitle>Check your email</AlertTitle>
              <AlertDescription>
                A temporary password has been sent to {email}. Use it to sign in and set a new password — redirecting to sign in…
              </AlertDescription>
            </Alert>
          )}
          {errorText && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Request failed</AlertTitle>
              <AlertDescription>{errorText}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy || success}
                aria-invalid={fieldError ? true : undefined}
              />
              {fieldError && (
                <p className="text-xs text-red-600">{fieldError}</p>
              )}
            </div>
            <Button
              type="submit"
              disabled={busy || success || secondsRemaining > 0}
              className="w-full"
            >
              {success ? 'Sent!' : busy ? 'Sending…' : secondsRemaining > 0 ? `Try again in ${secondsRemaining}s` : 'Send temporary password'}
            </Button>
            {secondsRemaining > 0 && (
              <p className="text-xs text-slate-500 text-center mt-2">
                For security, please wait before requesting again.
              </p>
            )}
          </form>
          <div className="mt-4 text-center text-sm">
            <span className="text-slate-600 dark:text-slate-400">Remember your password? </span>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-brand-600 hover:text-brand-700 font-medium cursor-pointer"
            >
              Sign in
            </button>
          </div>
        </CardContent>
      </Card>
        </div>
      </div>
    </div>
  );
}
